import { useMemo } from "react";
import { calculateRentalAnalysis } from "../utils/rentalAnalysisCalculator";

export default function RentalValueAnalysis({
  property,
  prediction,
  propertyAnalysis,
  mlRent,
  agentRent,
  lowRent,
  highRent,
  discountedPrice,
  mortgageConfig
}) {
  // Calculate rental value analysis using utility function
  // Now using lowRent and highRent which respect radio button selections
  const analysis = useMemo(() => {
    return calculateRentalAnalysis({
      property,
      prediction,
      propertyAnalysis,
      mlRent,
      agentRent,
      userRent: highRent, // highRent is computed from radio button selection
      discountedPrice,
      lowRent, // Pass lowRent from radio button selection
      highRent, // Pass highRent from radio button selection
      mortgageConfig, // Global config with separate P&I and I/O rates
    });
  }, [property, prediction, propertyAnalysis, mlRent, agentRent, highRent, lowRent, discountedPrice, mortgageConfig]);

  if (!analysis) return null;

  // Get property price for break-even down payment dollar amount calculation
  const propertyPrice = discountedPrice || property?.price || property?.asking_price || 0;

  // Styles
  const containerStyle = {
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "8px",
    border: "2px solid #000",
    marginBottom: "20px",
  };

  const titleStyle = {
    fontSize: "18px",
    fontWeight: "700",
    marginBottom: "16px",
    color: "#333",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  };

  const thStyle = {
    textAlign: "center",
    padding: "10px 8px",
    borderBottom: "2px solid #000",
    fontWeight: "700",
    color: "#333",
    backgroundColor: "#f8f9fa",
  };

  const tdStyle = {
    textAlign: "center",
    padding: "10px 8px",
    borderBottom: "1px solid #e0e0e0",
  };

  const firstColumnStyle = {
    ...tdStyle,
    textAlign: "left",
    fontWeight: "600",
    backgroundColor: "#f8f9fa",
  };

  const formatCurrency = (value) => {
    // Fix negative zero display
    const normalizedValue = value === 0 ? 0 : value;
    return normalizedValue < 0 ? `-$${Math.abs(normalizedValue).toLocaleString()}` : `$${normalizedValue.toLocaleString()}`;
  };

  const formatPercent = (value) => {
    // Fix negative zero display
    const normalizedValue = value === 0 ? 0 : value;
    return `${normalizedValue.toFixed(2)}%`;
  };

  const formatBreakEvenDownPayment = (breakEvenPct, propertyPrice) => {
    if (breakEvenPct === null) return "N/A";
    // If break-even is less than 1%, show "Full Financing Possible"
    if (breakEvenPct < 1) {
      return (
        <>
          0%<br />
          Full Financing Possible
        </>
      );
    }
    const downPaymentAmount = Math.round((propertyPrice * breakEvenPct) / 100);
    return (
      <>
        {formatPercent(breakEvenPct)}<br />
        {formatCurrency(downPaymentAmount)}
      </>
    );
  };

  const getCashflowColor = (value) => {
    return value >= 0 ? "#28a745" : "#dc3545";
  };

  return (
    <div style={containerStyle}>
      <div style={titleStyle}>Rental Value Analysis</div>

      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={{ ...thStyle, textAlign: "left" }}>Scenario</th>
            <th style={thStyle}>Low Rent<br/>P&I @ {analysis.piRate}%</th>
            <th style={thStyle}>High Rent<br/>P&I @ {analysis.piRate}%</th>
            <th style={thStyle}>Low Rent<br/>I/O @ {analysis.ioRate}%</th>
            <th style={thStyle}>High Rent<br/>I/O @ {analysis.ioRate}%</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={firstColumnStyle}>Rent</td>
            <td style={tdStyle}>{formatCurrency(analysis.lowPI.rent)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.highPI.rent)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.lowIO.rent)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.highIO.rent)}</td>
          </tr>
          <tr>
            <td style={firstColumnStyle}>Monthly Mortgage</td>
            <td style={tdStyle}>{formatCurrency(analysis.lowPI.mortgage)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.highPI.mortgage)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.lowIO.mortgage)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.highIO.mortgage)}</td>
          </tr>
          <tr>
            <td style={firstColumnStyle}>Monthly Carry</td>
            <td style={tdStyle}>{formatCurrency(analysis.lowPI.carry)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.highPI.carry)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.lowIO.carry)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.highIO.carry)}</td>
          </tr>
          <tr>
            <td style={firstColumnStyle}>Net Cashflow</td>
            <td style={{ ...tdStyle, fontWeight: "700", color: getCashflowColor(analysis.lowPI.cashflow) }}>
              {formatCurrency(analysis.lowPI.cashflow)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "700", color: getCashflowColor(analysis.highPI.cashflow) }}>
              {formatCurrency(analysis.highPI.cashflow)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "700", color: getCashflowColor(analysis.lowIO.cashflow) }}>
              {formatCurrency(analysis.lowIO.cashflow)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "700", color: getCashflowColor(analysis.highIO.cashflow) }}>
              {formatCurrency(analysis.highIO.cashflow)}
            </td>
          </tr>
          <tr>
            <td style={firstColumnStyle}>Cap Rate (%)</td>
            <td style={tdStyle}>{formatPercent(analysis.lowPI.capRate)}</td>
            <td style={tdStyle}>{formatPercent(analysis.highPI.capRate)}</td>
            <td style={tdStyle}>{formatPercent(analysis.lowIO.capRate)}</td>
            <td style={tdStyle}>{formatPercent(analysis.highIO.capRate)}</td>
          </tr>
          <tr>
            <td style={firstColumnStyle}>IRR - Year 7</td>
            <td style={tdStyle}>-</td>
            <td style={tdStyle}>-</td>
            <td style={{ ...tdStyle, fontWeight: "700", color: "#28a745" }}>
              {formatPercent(analysis.lowIO.irrYear7)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "700", color: "#28a745" }}>
              {formatPercent(analysis.highIO.irrYear7)}
            </td>
          </tr>
          <tr>
            <td style={firstColumnStyle}>Break-Even Down Payment</td>
            <td style={{ ...tdStyle, fontWeight: "600", color: "#0066cc" }}>
              {formatBreakEvenDownPayment(analysis.lowPI.breakEvenDownPayment, propertyPrice)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "600", color: "#0066cc" }}>
              {formatBreakEvenDownPayment(analysis.highPI.breakEvenDownPayment, propertyPrice)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "600", color: "#0066cc" }}>
              {formatBreakEvenDownPayment(analysis.lowIO.breakEvenDownPayment, propertyPrice)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "600", color: "#0066cc" }}>
              {formatBreakEvenDownPayment(analysis.highIO.breakEvenDownPayment, propertyPrice)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
